if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      phantomVariantChangeUnsubscriber = undefined;

      constructor() {
        super();

        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        /*
         * PHANTOM WICK
         *
         * Listen for Design changes inside the Quick Add modal.
         * This uses event delegation so it keeps working even when
         * Dawn replaces the variant picker after a selection.
         */
        this.handlePhantomDesignChange =
          this.handlePhantomDesignChange.bind(this);

        this.modalContent.addEventListener(
          'change',
          this.handlePhantomDesignChange
        );
      }

      hide(preventFocus = false) {
        const cartNotification =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        if (cartNotification) {
          cartNotification.setActiveElement(this.openedBy);
        }

        /*
         * Remove the temporary variant-change subscription
         * whenever the Quick Add modal closes.
         */
        this.phantomVariantChangeUnsubscriber?.();
        this.phantomVariantChangeUnsubscriber = undefined;

        this.modalContent.innerHTML = '';

        if (preventFocus) {
          this.openedBy = null;
        }

        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        const spinner = opener.querySelector('.loading__spinner');

        if (spinner) {
          spinner.classList.remove('hidden');
        }

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(
              responseText,
              'text/html'
            );

            const productElement =
              responseHTML.querySelector('product-info');

            if (!productElement) return;

            this.preprocessHTML(productElement);

            HTMLUpdateUtility.setInnerHTML(
              this.modalContent,
              productElement.outerHTML
            );

            /*
             * PHANTOM WICK
             *
             * After the product has been inserted into Quick Add,
             * begin listening for Dawn's completed variant updates.
             */
            this.setupPhantomVariantListener();

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }

            if (window.ProductModel) {
              window.ProductModel.loadShopifyXR();
            }

            super.show(opener);

            /*
             * Make the large Quick Add image match whichever
             * Design is currently selected when the modal opens.
             */
            requestAnimationFrame(() => {
              this.syncPhantomMediaToSelection();
            });
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');

            const spinner = opener.querySelector('.loading__spinner');

            if (spinner) {
              spinner.classList.add('hidden');
            }
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (
            classApplied.startsWith('color-') ||
            classApplied === 'gradient'
          ) {
            this.modalContent.classList.add(classApplied);
          }
        });

        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability =
          productElement.querySelector('pickup-availability');

        if (pickupAvailability) {
          pickupAvailability.remove();
        }

        const productModal =
          productElement.querySelector('product-modal');

        if (productModal) {
          productModal.remove();
        }

        const modalDialogs =
          productElement.querySelectorAll('modal-dialog');

        if (modalDialogs) {
          modalDialogs.forEach((modal) => modal.remove());
        }
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;

        productElement.innerHTML =
          productElement.innerHTML.replaceAll(oldId, newId);

        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(
              attribute.name,
              attribute.value.replace(oldId, newId)
            );
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList =
          productElement.querySelector('[id^="Slider-Gallery"]');

        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');

        galleryList
          .querySelectorAll('[id^="Slide-"]')
          .forEach((li) => {
            li.setAttribute('role', 'presentation');
          });
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');

        const desktopColumns =
          product?.classList.contains('product--columns');

        if (!desktopColumns) return;

        const mediaImages =
          product.querySelectorAll('.product__media img');

        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes =
            mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes =
            mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => {
          img.setAttribute('sizes', mediaImageSizes);
        });
      }

      /*
       * ============================================================
       * PHANTOM WICK QUICK ADD DESIGN IMAGE SYSTEM
       * ============================================================
       */

      setupPhantomVariantListener() {
        /*
         * Prevent duplicate subscriptions if Quick Add
         * is opened more than once.
         */
        this.phantomVariantChangeUnsubscriber?.();

        /*
         * Dawn publishes variantChange AFTER it finishes
         * updating the product.
         *
         * We intentionally wait for this event and then
         * reapply our Phantom Wick bottle image.
         */
        this.phantomVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          () => {
            requestAnimationFrame(() => {
              this.syncPhantomMediaToSelection();
            });
          }
        );
      }

      handlePhantomDesignChange(event) {
        const target = event.target;

        if (!target) return;

        const mediaId =
          this.getPhantomMediaIdFromTarget(target);

        /*
         * Ignore quantity changes and normal product controls.
         * We only care about options containing
         * data-phantom-media-id.
         */
        if (!mediaId) return;

        const productInfo =
          target.closest('product-info') ||
          this.modalContent.querySelector('product-info');

        if (!productInfo) return;

        /*
         * Store the chosen Design media ID so it survives
         * Dawn replacing the variant picker markup.
         */
        productInfo.dataset.phantomQuickAddMediaId = mediaId;

        /*
         * Change the picture immediately for responsiveness.
         */
        this.activatePhantomMedia(
          productInfo,
          mediaId
        );
      }

      syncPhantomMediaToSelection() {
        const productInfo =
          this.modalContent.querySelector('product-info');

        if (!productInfo) return;

        const mediaId =
          this.getSelectedPhantomMediaId(productInfo);

        if (!mediaId) return;

        this.activatePhantomMedia(
          productInfo,
          mediaId
        );
      }

      getPhantomMediaIdFromTarget(target) {
        /*
         * Radio-button Design picker.
         */
        if (target.dataset?.phantomMediaId) {
          return target.dataset.phantomMediaId;
        }

        /*
         * Dropdown fallback.
         */
        if (
          target.tagName === 'SELECT' &&
          target.selectedOptions?.length
        ) {
          return (
            target.selectedOptions[0].dataset
              ?.phantomMediaId || null
          );
        }

        return null;
      }

      getSelectedPhantomMediaId(productInfo) {
        /*
         * Find the currently checked circular Design option.
         */
        const checkedOption =
          productInfo.querySelector(
            'variant-selects input[data-phantom-media-id]:checked'
          );

        if (checkedOption?.dataset.phantomMediaId) {
          return checkedOption.dataset.phantomMediaId;
        }

        /*
         * Dropdown fallback.
         */
        const selects =
          productInfo.querySelectorAll(
            'variant-selects select'
          );

        for (const select of selects) {
          const selected =
            select.selectedOptions?.[0];

          if (selected?.dataset.phantomMediaId) {
            return selected.dataset.phantomMediaId;
          }
        }

        /*
         * If Dawn just replaced the picker and we have not
         * found the new checked input yet, use the last
         * selected Phantom Wick media ID.
         */
        if (
          productInfo.dataset.phantomQuickAddMediaId
        ) {
          return productInfo.dataset
            .phantomQuickAddMediaId;
        }

        return null;
      }

      activatePhantomMedia(productInfo, mediaId) {
        if (!mediaId) return;

        const mediaGallery =
          productInfo.querySelector('media-gallery');

        if (!mediaGallery) return;

        /*
         * Find every actual product media slide.
         */
        let mediaItems = Array.from(
          mediaGallery.querySelectorAll(
            '.product__media-item[data-media-id]'
          )
        );

        /*
         * Fallback for slightly different Dawn versions.
         */
        if (!mediaItems.length) {
          mediaItems = Array.from(
            mediaGallery.querySelectorAll(
              '[data-media-id]'
            )
          );
        }

        if (!mediaItems.length) return;

        /*
         * IMPORTANT:
         *
         * Do not build the ID using the section name.
         * Quick Add rewrites section IDs with "quickadd-",
         * which can cause Dawn's normal variant image
         * switching to miss the correct slide.
         *
         * Instead we find the slide by its actual Shopify
         * media ID at the end of data-media-id.
         */
        const targetMedia = mediaItems.find((item) => {
          const itemMediaId =
            String(item.dataset.mediaId || '');

          return (
            itemMediaId === String(mediaId) ||
            itemMediaId.endsWith(`-${mediaId}`)
          );
        });

        if (!targetMedia) return;

        /*
         * First use Dawn's own MediaGallery function
         * with the EXACT media ID that exists in the
         * Quick Add DOM.
         */
        if (
          typeof mediaGallery.setActiveMedia === 'function'
        ) {
          try {
            mediaGallery.setActiveMedia(
              targetMedia.dataset.mediaId,
              false
            );
          } catch (error) {
            console.warn(
              'Phantom Wick Quick Add media fallback used.',
              error
            );
          }
        }

        /*
         * Then enforce the active state ourselves.
         *
         * This makes the system work even if Dawn's
         * Quick Add media logic fails because of its
         * rewritten section IDs.
         */
        mediaItems.forEach((item) => {
          if (item === targetMedia) {
            item.classList.add('is-active');
            item.removeAttribute('aria-hidden');
          } else {
            item.classList.remove('is-active');
            item.setAttribute('aria-hidden', 'true');
          }
        });

        /*
         * Reapply once more on the next frame.
         * This prevents Dawn's asynchronous variant update
         * from immediately putting the scent card back.
         */
        requestAnimationFrame(() => {
          mediaItems.forEach((item) => {
            if (item === targetMedia) {
              item.classList.add('is-active');
              item.removeAttribute('aria-hidden');
            } else {
              item.classList.remove('is-active');
              item.setAttribute('aria-hidden', 'true');
            }
          });
        });
      }
    }
  );
}